import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ToastrService } from 'ngx-toastr';

import { AuthService, ThemeService } from '../../../core/services';
import { AuthStore } from '../../../core/stores';
import { EntrySkeletonComponent } from '../shared/entry-skeleton/entry-skeleton.component';
import { CredentialsData, DiscoveryResponse } from '../../../core/models';
import * as Paths from '../../../core/constants';
import { FormErrorStateMatcher } from '../../../core/utils';
import { FocusDirective } from '../../main/shared/directives/focus.directive';
import { MatButton } from '@angular/material/button';
import { LoginMethod, Role } from '../../../core/enums';
import { HttpErrorResponse } from '@angular/common/http';
import { ResultBannerComponent } from '../../main/shared/components';

@Component({
  selector: 'dbx-login',
  standalone: true,
  imports: [
    EntrySkeletonComponent,
    MatIcon,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinner,
    RouterLink,
    FocusDirective,
    MatButton,
    ResultBannerComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  host: {
    class: 'entry-page',
  },
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthStore);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(FormBuilder);

  verify2FA = signal<boolean>(false);
  qrCodeUrl = signal<string>('');
  ssoUserNotExists = signal<boolean | undefined>(undefined);
  temporaryPasswordExpired = signal<boolean>(false);
  qrCodeSecret = computed(() => {
    try {
      const url = new URL(this.qrCodeUrl());
      const data = url.searchParams.get('data');
      if (!data) return null;
      const dataUrl = new URL(data);
      return dataUrl.searchParams.get('secret');
    } catch {
      return null;
    }
  });
  singleSignOn = signal<boolean>(true);
  ssoLogin = signal<boolean>(false);
  loading = signal<boolean>(false);
  showPassword = signal<boolean>(false);
  submitted = signal<boolean>(false);
  credentials = {} as CredentialsData;

  errorStateMatcher: FormErrorStateMatcher = new FormErrorStateMatcher(this.submitted);

  readonly Paths = Paths;

  loginForm = this.formBuilder.group({
    username: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  verifyCodeForm = this.formBuilder.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
  });

  get username(): AbstractControl<string | null, string | null> | null {
    return this.loginForm.get('username');
  }

  get password(): AbstractControl<string | null, string | null> | null {
    return this.loginForm.get('password');
  }

  get code(): AbstractControl<string | null, string | null> | null {
    return this.verifyCodeForm.get('code');
  }

  constructor() {
    if (this.authStore.authenticated()) {
      this.router.navigate([Paths.DASHBOARD_PATH]).then();
    }
  }

  ngOnInit(): void {
    this.themeService.forceDarkTheme();
  }

  ngOnDestroy(): void {
    this.themeService.init();
  }

  submit() {
    this.submitted.set(true);
    this.ssoUserNotExists.set(false);
    if (this.invalidForm()) {
      return;
    }
    this.loading.set(true);
    if (this.singleSignOn()) {
      this.authService.discovery(this.username?.value ?? '').subscribe({
        next: (res: DiscoveryResponse) => {
          const isSsoLogin = res.loginMethod === LoginMethod.OIDC;
          if (isSsoLogin) {
            this.authService.setNewSsoProvider(res.clientId, res.tenantId).subscribe({
              complete: () => {
                this.ssoLogin.set(true);
                this.loading.set(false);
              },
            });
          } else {
            this.submitted.set(false);
            this.singleSignOn.set(false);
            this.loading.set(false);
          }
        },
        error: () => {
          this.loading.set(false);
          this.toastrService.error(this.translateService.instant('common.alerts.genericError'));
        },
      });
    } else {
      this.executeLogin();
    }
  }

  executeSSOLogin() {
    this.loading.set(true);
    this.executeLogin();
  }

  private executeLogin() {
    (this.singleSignOn() ? this.loginWithSSO() : this.login()).subscribe({
      next: (authResponse) => {
        this.qrCodeUrl.set(authResponse.qrCodeUrl);
        if (authResponse.resetKey) {
          this.router
            .navigate([Paths.RESET_PASSWORD_PATH], { queryParams: { resetKey: authResponse.resetKey } })
            .then();
          return;
        }
        if (authResponse.verify2FA) {
          this.reset();
          this.submitted.set(false);
          this.verify2FA.set(authResponse.verify2FA);
          this.loading.set(false);
          return;
        }
        this.authService.getCurrentUser().subscribe({
          next: (user) => {
            let redirectUrl = [Paths.CYBERSECURITY_COCKPIT_PATH];
            switch (user.role) {
              case Role.GlobalAdmin:
                redirectUrl = [Paths.ADMIN_COCKPIT_PATH];
                break;
              case Role.Partner:
                redirectUrl = [Paths.PARTNER_COCKPIT_PATH];
                break;
              case Role.User:
                redirectUrl = [Paths.CYBERSECURITY_COCKPIT_PATH];
                break;
            }

            this.router.navigate(redirectUrl).then();
          },
          error: () => this.toastrService.error(this.translateService.instant('common.alerts.genericError')),
          complete: () => this.loading.set(false),
        });
      },
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);

        if (error.status == 404) {
          // SSO user does not exist
          this.ssoUserNotExists.set(true);
          return;
        }

        if (error.status == 400 && error.error.detail === 'Temporary password expired') {
          this.temporaryPasswordExpired.set(true);
          return;
        }

        const errorKey = this.getErrorKey(error);
        error.status === 409
          ? this.toastrService.warning(this.translateService.instant(errorKey))
          : this.toastrService.error(this.translateService.instant(errorKey));
      },
    });
  }

  private getErrorKey(error: HttpErrorResponse): string {
    switch (error.status) {
      case 401: {
        if (error.error.detail === 'Invalid code') {
          return 'login.alerts.invalidCode';
        }

        return `common.alerts.${this.singleSignOn() ? 'ssoUnauthorized' : 'unauthorized'}`;
      }
      case 409:
        return 'login.alerts.customerNotConfigured';
      case 423:
        return 'bfp.userLocked';
      case 429:
        return 'bfp.tooManyAttempts';
      case 500:
        return 'common.alerts.internalError';
      default:
        return 'common.alerts.genericError';
    }
  }

  private login() {
    this.credentials = this.verify2FA()
      ? { ...this.credentials, ...this.verifyCodeForm.value }
      : { ...this.loginForm.value };
    return this.authService.login(this.credentials);
  }

  private loginWithSSO() {
    const { username } = this.loginForm.value;
    return this.authService.singleSignOn(username ?? '');
  }

  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  loginBack(): void {
    this.reset();
    this.singleSignOn.set(true);
  }

  reset() {
    this.loginForm.reset();
    this.verifyCodeForm.reset();
    this.verify2FA.set(false);
    this.ssoLogin.set(false);
  }

  invalidForm = () => {
    if (this.singleSignOn()) {
      return this.username?.invalid;
    }
    if (this.verify2FA()) {
      return this.verifyCodeForm.invalid;
    }
    return this.loginForm.invalid;
  };
}
